import React, { useEffect, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublicIcon from '@mui/icons-material/Public';
import { QRCodeCanvas } from 'qrcode.react';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';

export function buildSurveyPublicUrl(token) {
  return `${window.location.origin}/survey/p/s/${token}`;
}

// Location fields collected before generating a link/QR — all optional.
// [key, label, maxLength] — maxLength mirrors the backend column sizes.
const LOCATION_FIELDS = [
  ['client', 'Client', 300],
  ['country', 'Country', 200],
  ['city', 'City', 200],
  ['building', 'Building', 300],
  ['floor', 'Floor', 100],
  ['facility', 'Facility', 300],
];

const EMPTY_FORM = { client: '', country: '', city: '', building: '', floor: '', facility: '' };

// Mirrors the backend's deliberately loose check — catches typos, not RFC edge cases.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function getShareNote({ isLiveWindowOpen, liveWindowState }) {
  if (isLiveWindowOpen || liveWindowState === 'open') {
    return 'The live window is open — the link accepts responses now.';
  }
  if (liveWindowState === 'not_open') {
    return 'Share ahead of time. Responses are accepted only during the scheduled window.';
  }
  return 'The link stops accepting responses once the live window closes.';
}

// Human-readable one-line summary of a link's location (or a plain-link label).
function locationSummary(link) {
  return (
    [
      link.client,
      link.facility,
      link.building,
      link.floor ? `Floor ${link.floor}` : '',
      link.city,
      link.country,
    ]
      .map((s) => (s || '').trim())
      .filter(Boolean)
      .join(' · ') ||
    (link.access_mode === 'restricted' ? 'Secure link (no location)' : 'Public link (no location)')
  );
}

/** Split free text on commas/semicolons/whitespace into candidate emails. */
function splitEmails(text) {
  return String(text || '')
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Chip-style email list input. Type or paste addresses (comma / semicolon /
 * space separated) and press Enter — invalid ones surface an inline error.
 */
function EmailChipsInput({ emails, onChange, disabled, inputId }) {
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  function commitText(raw) {
    const parts = splitEmails(raw);
    if (!parts.length) return;
    const bad = parts.find((p) => !EMAIL_RE.test(p));
    if (bad) {
      setError(`'${bad}' is not a valid email address.`);
      setText(raw);
      return;
    }
    const merged = [...emails];
    parts.forEach((p) => {
      if (!merged.includes(p)) merged.push(p);
    });
    onChange(merged);
    setText('');
    setError('');
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.35rem',
          alignItems: 'center',
          border: '1px solid var(--line)',
          borderRadius: 8,
          padding: '0.4rem 0.5rem',
        }}
      >
        {emails.map((email) => (
          <span
            key={email}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
              background: 'var(--panel)',
              border: '1px solid var(--line)',
              borderRadius: 999,
              padding: '0.1rem 0.3rem 0.1rem 0.6rem',
              fontSize: '0.85rem',
            }}
          >
            {email}
            {!disabled && (
              <button
                type="button"
                className="btn small-btn icon-btn"
                style={{ border: 'none', padding: 2, minWidth: 0 }}
                onClick={() => onChange(emails.filter((e) => e !== email))}
                aria-label={`Remove ${email}`}
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            )}
          </span>
        ))}
        <input
          id={inputId}
          type="text"
          value={text}
          disabled={disabled}
          placeholder={emails.length ? 'Add another…' : 'name@company.com, name2@company.com'}
          autoComplete="off"
          style={{ flex: 1, minWidth: 180, border: 'none', outline: 'none', padding: '0.25rem' }}
          onChange={(e) => {
            setText(e.target.value);
            if (error) setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
              e.preventDefault();
              commitText(text);
            } else if (e.key === 'Backspace' && !text && emails.length) {
              onChange(emails.slice(0, -1));
            }
          }}
          onBlur={() => commitText(text)}
          onPaste={(e) => {
            e.preventDefault();
            commitText(text + ' ' + e.clipboardData.getData('text'));
          }}
        />
      </div>
      {error && (
        <div className="field-error" role="alert" style={{ marginTop: '0.35rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}

function AccessBadge({ link }) {
  const restricted = link.access_mode === 'restricted';
  return (
    <span
      className="muted small"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
      title={
        restricted
          ? `Only ${link.recipients?.length || 0} invited ${
              (link.recipients?.length || 0) === 1 ? 'person' : 'people'
            } can open this link`
          : 'Anyone with the link can respond'
      }
    >
      {restricted ? (
        <LockOutlinedIcon style={{ fontSize: 14 }} />
      ) : (
        <PublicIcon style={{ fontSize: 14 }} />
      )}
      {restricted ? `Secure · ${link.recipients?.length || 0} invited` : 'Public'}
    </span>
  );
}

/**
 * Modal to share a survey. Two access modes:
 *   - Anyone with the link (public, anonymous)
 *   - Specific people only (secure): recipients sign in with their JLL (Okta)
 *     account and must be on the invite list to open the survey.
 * Location details (client/country/city/building/floor/facility) are optional
 * in both modes — enter them to tag responses with a location for analytics.
 */
export default function SurveyShareDialog({
  open,
  onClose,
  surveyId,
  surveyTitle,
  isLiveWindowOpen,
  liveWindowState,
  onCopySuccess,
  onCopyError,
}) {
  const { apiFetch } = useSurveyRuntimeApi();

  const [form, setForm] = useState(EMPTY_FORM);
  const [accessMode, setAccessMode] = useState('public');
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [formError, setFormError] = useState('');
  // The link whose QR is currently displayed (from a generate or a list "Show QR").
  const [generatedLink, setGeneratedLink] = useState(null);
  // distribution_id of the restricted link whose recipients are being edited.
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editEmails, setEditEmails] = useState([]);
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Load existing share links (and reset transient state) whenever the
  // dialog opens for a survey.
  useEffect(() => {
    if (!open || !surveyId) return undefined;
    setForm(EMPTY_FORM);
    setAccessMode('public');
    setRecipientEmails([]);
    setFormError('');
    setGeneratedLink(null);
    setEditingLinkId(null);
    setEditError('');
    let cancelled = false;
    setLoadingLinks(true);
    apiFetch(`/admin/surveys/${surveyId}/location-links`)
      .then((data) => {
        if (!cancelled) setLinks(data?.links || []);
      })
      .catch(() => {
        if (!cancelled) setLinks([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLinks(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, surveyId, apiFetch]);

  if (!open || !surveyId) return null;

  const note = getShareNote({ isLiveWindowOpen, liveWindowState });
  const generatedUrl = generatedLink ? buildSurveyPublicUrl(generatedLink.public_token) : '';

  function copyUrl(url) {
    void navigator.clipboard
      .writeText(url)
      .then(() => onCopySuccess?.())
      .catch(() => onCopyError?.());
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function refreshLinks() {
    const data = await apiFetch(`/admin/surveys/${surveyId}/location-links`);
    setLinks(data?.links || []);
    return data?.links || [];
  }

  async function generateLink(e) {
    e.preventDefault();
    setFormError('');
    if (accessMode === 'restricted' && recipientEmails.length === 0) {
      setFormError('Add at least one email to share with specific people.');
      return;
    }
    setGenerating(true);
    try {
      const created = await apiFetch(`/admin/surveys/${surveyId}/location-links`, {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          access_mode: accessMode,
          recipient_emails: accessMode === 'restricted' ? recipientEmails : [],
        }),
      });
      await refreshLinks();
      setGeneratedLink(created);
      setForm(EMPTY_FORM);
      setRecipientEmails([]);
    } catch (err) {
      setFormError(err?.message || 'Could not generate the link.');
    } finally {
      setGenerating(false);
    }
  }

  function startEditRecipients(link) {
    setEditingLinkId(link.distribution_id);
    setEditEmails(link.recipients || []);
    setEditError('');
  }

  async function saveRecipients(link) {
    if (!editEmails.length) {
      setEditError('A secure link needs at least one recipient.');
      return;
    }
    setSavingRecipients(true);
    setEditError('');
    try {
      await apiFetch(
        `/admin/surveys/${surveyId}/share-links/${link.distribution_id}/recipients`,
        { method: 'PUT', body: JSON.stringify({ recipient_emails: editEmails }) },
      );
      const fresh = await refreshLinks();
      setEditingLinkId(null);
      if (generatedLink?.distribution_id === link.distribution_id) {
        setGeneratedLink(
          fresh.find((l) => l.distribution_id === link.distribution_id) || generatedLink,
        );
      }
    } catch (err) {
      setEditError(err?.message || 'Could not update recipients.');
    } finally {
      setSavingRecipients(false);
    }
  }

  return (
    <div
      className="modal-backdrop modal-backdrop--publish-success"
      role="dialog"
      aria-modal="true"
      aria-labelledby="survey-share-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-card card modal-card--publish-success"
        style={{ maxHeight: '85vh', overflowY: 'auto' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="survey-share-title">Share survey</h2>
        <p className="muted small publish-success-lede">
          {surveyTitle ? (
            <>
              Generate a link/QR to share <strong>{surveyTitle}</strong>.
            </>
          ) : (
            'Generate a link/QR to share this survey.'
          )}{' '}
          Location details are optional — add them to tag responses with a location for analytics.
        </p>
        <p className="muted small" style={{ marginTop: 0 }}>
          {note}
        </p>

        <form onSubmit={generateLink}>
          <fieldset style={{ border: 'none', margin: '0 0 0.75rem', padding: 0 }}>
            <legend className="muted small" style={{ marginBottom: '0.35rem' }}>
              Who can respond
            </legend>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.35rem' }}>
              <input
                type="radio"
                name="share-access-mode"
                value="public"
                checked={accessMode === 'public'}
                onChange={() => setAccessMode('public')}
                style={{ marginTop: 3 }}
              />
              <span>
                Anyone with the link
                <span className="muted small" style={{ display: 'block' }}>
                  No sign-in needed — responses stay anonymous.
                </span>
              </span>
            </label>
            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <input
                type="radio"
                name="share-access-mode"
                value="restricted"
                checked={accessMode === 'restricted'}
                onChange={() => setAccessMode('restricted')}
                style={{ marginTop: 3 }}
              />
              <span>
                Specific people only (secure)
                <span className="muted small" style={{ display: 'block' }}>
                  Recipients sign in with their JLL account — nobody else can open the link.
                </span>
              </span>
            </label>
          </fieldset>

          {accessMode === 'restricted' && (
            <div className="field" style={{ marginBottom: '0.75rem' }}>
              <span>
                Share with <span className="muted">(email addresses)</span>
              </span>
              <EmailChipsInput
                emails={recipientEmails}
                onChange={setRecipientEmails}
                disabled={generating}
                inputId="share-recipients-input"
              />
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '0.75rem',
            }}
          >
            {LOCATION_FIELDS.map(([key, label, maxLength]) => (
              <label className="field" key={key}>
                <span>
                  {label} <span className="muted">(optional)</span>
                </span>
                <input
                  type="text"
                  value={form[key]}
                  maxLength={maxLength}
                  autoComplete="off"
                  onChange={(e) => updateField(key, e.target.value)}
                />
              </label>
            ))}
          </div>
          {formError && (
            <div className="field-error" role="alert" style={{ marginTop: '0.5rem' }}>
              {formError}
            </div>
          )}
          <div style={{ marginTop: '0.75rem' }}>
            <button type="submit" className="btn primary" disabled={generating}>
              {generating ? 'Generating…' : 'Generate link'}
            </button>
          </div>
        </form>

        {generatedLink && (
          <div className="publish-success-body" style={{ marginTop: '1rem' }}>
            <div className="builder-link-head">
              <div className="muted small">
                {locationSummary(generatedLink)} <AccessBadge link={generatedLink} />
              </div>
              <button
                type="button"
                className="btn small-btn icon-btn"
                onClick={() => copyUrl(generatedUrl)}
                aria-label="Copy link"
              >
                <ContentCopyIcon fontSize="small" />
              </button>
            </div>
            <code className="builder-link">{generatedUrl}</code>
            {generatedLink.access_mode === 'restricted' && (
              <p className="muted small" style={{ margin: '0.35rem 0 0' }}>
                Only {generatedLink.recipients?.join(', ')} can open this link after signing in.
              </p>
            )}
            <div className="builder-link-qr">
              <div className="builder-qr-box" aria-label="QR code for survey link">
                <QRCodeCanvas value={generatedUrl} size={160} includeMargin />
              </div>
            </div>
          </div>
        )}

        {(loadingLinks || links.length > 0) && (
          <div style={{ marginTop: '1.25rem' }}>
            <div className="muted small" style={{ marginBottom: '0.5rem' }}>
              Existing share links
            </div>
            {loadingLinks ? (
              <div className="muted small">Loading…</div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }}>
                {links.map((link) => (
                  <li
                    key={link.distribution_id}
                    style={{
                      border: '1px solid var(--line)',
                      borderRadius: 12,
                      padding: '0.6rem 0.75rem',
                      background:
                        generatedLink && link.public_token === generatedLink.public_token
                          ? 'var(--panel)'
                          : 'transparent',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.75rem',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {locationSummary(link)}
                        </div>
                        <div className="muted small" style={{ display: 'flex', gap: '0.6rem' }}>
                          <AccessBadge link={link} />
                          <span>
                            {link.response_count} response{link.response_count === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                        {link.access_mode === 'restricted' && (
                          <button
                            type="button"
                            className="btn small-btn"
                            onClick={() =>
                              editingLinkId === link.distribution_id
                                ? setEditingLinkId(null)
                                : startEditRecipients(link)
                            }
                          >
                            {editingLinkId === link.distribution_id ? 'Cancel' : 'Edit people'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn small-btn"
                          onClick={() => setGeneratedLink(link)}
                        >
                          Show QR
                        </button>
                        <button
                          type="button"
                          className="btn small-btn icon-btn"
                          onClick={() => copyUrl(buildSurveyPublicUrl(link.public_token))}
                          aria-label="Copy share link"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                    {editingLinkId === link.distribution_id && (
                      <div style={{ marginTop: '0.6rem' }}>
                        <EmailChipsInput
                          emails={editEmails}
                          onChange={setEditEmails}
                          disabled={savingRecipients}
                          inputId={`edit-recipients-${link.distribution_id}`}
                        />
                        {editError && (
                          <div className="field-error" role="alert" style={{ marginTop: '0.35rem' }}>
                            {editError}
                          </div>
                        )}
                        <div style={{ marginTop: '0.5rem' }}>
                          <button
                            type="button"
                            className="btn primary small-btn"
                            disabled={savingRecipients}
                            onClick={() => saveRecipients(link)}
                          >
                            {savingRecipients ? 'Saving…' : 'Save people'}
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="builder-modal-actions publish-success-actions">
          <button type="button" className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
